import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const ANIMATION_DURATION_MS = 350;

/**
 * Reorders product grid items so products matching the selected topic float to the top,
 * without hiding any other products. Exactly one option is selected at a time: either "All"
 * (the default, no reordering) or a single topic.
 *
 * Animates the reorder with a self-contained FLIP animation (Web Animations API) rather than
 * the View Transition API, since document.startViewTransition is a page-wide singleton that
 * can be aborted by unrelated transitions elsewhere on the page (e.g. an announcement carousel).
 */
class TopicSortComponent extends Component {
  selectTopic(event) {
    const button = event.target.closest('[data-topic]');
    if (!button) return;

    const buttons = this.refs.buttons || [];
    for (const other of buttons) {
      other.setAttribute('aria-pressed', 'false');
    }
    if (this.refs.allButton) this.refs.allButton.setAttribute('aria-pressed', 'false');

    button.setAttribute('aria-pressed', 'true');

    this.#applyOrder();
  }

  clearTopics() {
    const buttons = this.refs.buttons || [];
    for (const other of buttons) {
      other.setAttribute('aria-pressed', 'false');
    }
    if (this.refs.allButton) this.refs.allButton.setAttribute('aria-pressed', 'true');

    this.#applyOrder();
  }

  #applyOrder() {
    const grid = document.querySelector('#ResultsList .product-grid');
    if (!grid) return;

    const items = [...grid.querySelectorAll('.product-grid__item')];
    const buttons = this.refs.buttons || [];
    const activeTopics = buttons
      .filter((button) => button.getAttribute('aria-pressed') === 'true')
      .map((button) => button.dataset.topic);

    const animate = !prefersReducedMotion();
    const firstRects = animate ? new Map(items.map((item) => [item, item.getBoundingClientRect()])) : null;

    for (const item of items) {
      const topics = (item.dataset.topics || '').split(' ').filter(Boolean);
      const matches = activeTopics.length === 0 || activeTopics.some((topic) => topics.includes(topic));
      item.style.order = matches ? '0' : '1';
    }

    if (!animate || !firstRects) return;

    for (const item of items) {
      const first = firstRects.get(item);
      if (!first) continue;

      const last = item.getBoundingClientRect();
      const deltaX = first.left - last.left;
      const deltaY = first.top - last.top;

      if (deltaX === 0 && deltaY === 0) continue;

      item.getAnimations().forEach((animation) => animation.cancel());
      item.animate(
        [{ transform: `translate(${deltaX}px, ${deltaY}px)` }, { transform: 'translate(0, 0)' }],
        { duration: ANIMATION_DURATION_MS, easing: 'ease' }
      );
    }
  }
}

if (!customElements.get('topic-sort-component')) {
  customElements.define('topic-sort-component', TopicSortComponent);
}
